import { useAtomValue } from "jotai";
import { Info } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import InfoModal from "../InfoModal";
import ToolTipWrapper from "../TooltipWrapper";
import style from "./style.module.css";
import { BatchPlan, BatchPlus, ComparisonTable, Offering, POP_UP_ACTION, VideoDetails } from "@components/Sales/batch.type";
import { planTableSelectedPlanAtom, userModeAtom } from "@utils/store/store";
import { cn, getCDNAssetUrl, getPlansDivWidth, getPlusDefaultPlan } from "@utils/utilities";
import { CHECK_GREEN, CHECK_GREEN_FADE, CROSS_RED, CROSS_RED_FADE, POLYGON_BLACK } from "@assets/images/location/ASSETS_IMAGES";

interface props {
  config: BatchPlus;
  selectedIndex: number;
  triggerEvent: (actionType: POP_UP_ACTION) => void;
  ctaName: string;
  pageName: string;
  slug: string;
  openInIframe?: boolean;
  onClose: () => void;
}

interface subContentType {
  description: string;
  displayName: string;
  videoDetails: VideoDetails;
}

const PlanDescription: FC<props> = ({
  config,
  selectedIndex,
  triggerEvent,
  ctaName,
  pageName,
  slug,
  openInIframe,
  onClose,
}) => {
  const userData = useAtomValue(userModeAtom);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [selectedContent, setSelectedContent] = useState<subContentType | null>(
    null,
  );
  const selectedPlan = useAtomValue(planTableSelectedPlanAtom);
  const content = config?.comparisonTable;
  const ref = useRef<(HTMLDivElement | null)[]>([]);
  const offeringColRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isFadedOut, setIsFadedOut] = useState(false);
  const [isWebScreen, setIsWebScreen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    const updateScreenSize = () => {
      setIsWebScreen(mediaQuery.matches);
    };
    updateScreenSize();
    mediaQuery.addEventListener("change", updateScreenSize);
    return () => {
      mediaQuery.removeEventListener("change", updateScreenSize);
    };
  }, []);

  const truncateTitle = (heading: string, wordLimit: number) => {
    return heading?.length > wordLimit
      ? heading.slice(0, wordLimit - 5) + "..."
      : heading;
  };

  const isFirstRender = useRef<Boolean>(true);

  useEffect(() => {
    if (selectedIndex > -1) {
      let scrollTimer1: ReturnType<typeof setTimeout>;
      let scrollTimer2: ReturnType<typeof setTimeout>;
      let fadeOutTimer: ReturnType<typeof setTimeout>;

      const scrollIntoView = () => {
        if (ref.current && ref.current[selectedIndex]) {
          ref.current[selectedIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      };

      const scrollParent = () => {
        if (offeringColRef.current && offeringColRef.current[selectedIndex]) {
          const element = offeringColRef.current[selectedIndex];
          const parent = offeringColRef.current[0]?.parentElement;

          if (parent) {
            const elementOffset = element?.offsetLeft || 0;
            const parentOffset = parent?.offsetLeft || 0;

            parent.scrollTo({
              left: elementOffset - parentOffset,
              behavior: "smooth",
            });
          }
        }
      };

      const handleFadeOut = () => {
        setIsFadedOut(true);
        fadeOutTimer = setTimeout(() => {
          setIsFadedOut(false);
        }, 700);
      };

      const viewRender = () => {
        scrollIntoView();
        scrollTimer2 = setTimeout(() => {
          scrollParent();
          handleFadeOut();
        }, 500);
      };

      if (isFirstRender.current) {
        scrollTimer1 = setTimeout(viewRender, 1500);
        isFirstRender.current = false;
      } else {
        viewRender();
      }

      return () => {
        // Clear all timers on cleanup.
        clearTimeout(scrollTimer1);
        clearTimeout(scrollTimer2);
        clearTimeout(fadeOutTimer);
      };
    }
  }, [selectedIndex]);

  const handleInfoClick = (subContent: subContentType) => {
    if (subContent?.description) {
      triggerEvent(POP_UP_ACTION.INFO_ICON);
      setOpenInfoModal(true);
      setSelectedContent(subContent);
    }
  };

  const fetchOfferings = () => {
    return content?.filter((item: ComparisonTable) => item?.bundleName);
  };

  const { div1Width, div2Width } = getPlansDivWidth(config?.plans?.length || 1);

  const handleDemoVideoRedirection = () => {
    triggerEvent(POP_UP_ACTION.KNOW_MORE);
    const defaultPlan = getPlusDefaultPlan(config);
    const currentPlan = config?.plans?.find(
      (plan: BatchPlan) => plan?.isPlanPurchased,
    );

    // if (openInIframe) {
    //   const payload = {
    //     action: IFRAME_ACTIONS.PARENT_NAVIAGTION,
    //     applicationType: ApplicationTypes.BATCH_PLUS_TABLE,
    //     redirectionType: "DEMO_VIDEOS",
    //     planId: selectedPlan?._id,
    //     batchSlug: slug,
    //     selectedPlan: selectedPlan?._id,
    //     cameFrom: ctaName,
    //   };

    //   postMessageToParent(payload, IFRAME_ACTIONS.PARENT_NAVIAGTION, "json");
    // } else {
    //   handleRedirectToDemoVideos({
    //     selectedPlan,
    //     defaultPlan,
    //     currentPlan,
    //     batchId: slug,
    //     navigate,
    //   });
    // }
    onClose();
  };

  return (
    <div
      className={cn(style.table_wrapper, {
        [style.bottom_space]: !selectedPlan?.isPlanPurchased,
      })}
    >
      <section className={cn(style.wrapper)}>
        <div
          className={style.headings_wrapper}
          style={{
            width: `${div1Width}%`,
          }}
        >
          <div className={style.cell_height_40}></div>
          <div
            className={cn(style.main_heading, style.cell_height_40, style.bold)}
          >
            What you get
          </div>
          {fetchOfferings()?.map(
            (
              table: ComparisonTable,
              tableIndex: number,
              self: ComparisonTable[],
            ) => (
              <div
                ref={(val) => (ref.current[tableIndex] = val)}
                key={tableIndex}
              >
                {tableIndex > 0 &&
                  !selectedPlan?.isPlanPurchased &&
                  selectedIndex === tableIndex &&
                  isFadedOut && (
                    <div
                      style={{
                        height: `${ref?.current[tableIndex]?.clientHeight}px`,
                      }}
                      className={style.animation_wrapper}
                    ></div>
                  )}
                {tableIndex > 0 && (
                  <div
                    className={cn(
                      style.bundle_name,
                      style.cell_height_40,
                      style.pt_2,
                    )}
                  >
                    {table?.bundleName}
                  </div>
                )}
                <section className={style.px_3}>
                  {table?.offering?.map(
                    (
                      offering: Offering,
                      offeringIndex: number,
                      offeringSelf: Offering[],
                    ) => (
                      <div
                        className={cn(
                          [style.sub_heading_wrapper, style.cell_height_40],
                          {
                            [style.bold]: offering?.isBold,
                            [style.pb_2]:
                              self.length - 1 !== tableIndex &&
                              offeringSelf?.length - 1 === offeringIndex,
                          },
                        )}
                        key={offeringIndex}
                      >
                        {offering?.offeringIcon?.webIcon && (
                          <span className={style.sub_heading_web_icon}>
                            <img src={offering?.offeringIcon?.webIcon} />
                          </span>
                        )}
                        <div
                          className={style.offering_sub_wrapper}
                          onClick={() => handleInfoClick(offering)}
                        >
                          <ToolTipWrapper
                            showTooltip={
                              offering?.displayName?.length >
                              (isWebScreen ? 50 : 20)
                            }
                            content={offering?.displayName}
                          >
                            <span
                              className={cn(style.text_truncate, {
                                [style.pointer]: offering?.description,
                              })}
                            >
                              {truncateTitle(
                                offering?.displayName,
                                isWebScreen ? 50 : 20,
                              )}
                            </span>
                          </ToolTipWrapper>

                          {offering?.description && (
                            <span
                              className={cn(style.hover_info, {
                                [style.pointer]: offering?.description,
                              })}
                            >
                              <Info height={14} width={14} />
                            </span>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </section>
                {self.length - 1 !== tableIndex && (
                  <div className={style.separate} />
                )}
              </div>
            ),
          )}
        </div>
        <div
          style={{
            width: `${div2Width}%`,
          }}
          className={style.plans_offering_wrapper}
        >
          {config?.plans?.map((plan: BatchPlan, planIndex: number) => (
            <div
              ref={(val) => (offeringColRef.current[planIndex] = val)}
              className={cn([style.offering_col], {
                [style.offering_col_active]: planIndex === selectedIndex,
              })}
              key={planIndex}
            >
              <div
                className={cn(
                  style.offered_plans_wrapper,
                  style.cell_height_40,
                )}
              >
                {plan?.headerIcon?.webIcon && (
                  <div className={style.plan_icon_wrapper}>
                    <img src={plan?.headerIcon?.webIcon} alt="icon" />
                  </div>
                )}
              </div>
              <div className={cn(style.offered_heading, style.cell_height_40)}>
                <ToolTipWrapper
                  showTooltip={plan?.title?.length > (isWebScreen ? 13 : 10)}
                  content={plan?.title}
                >
                  <span
                    className={cn([style.plain_text], {
                      [style.active_text]: selectedIndex === planIndex,
                    })}
                  >
                    {truncateTitle(plan?.title, isWebScreen ? 13 : 10)}
                  </span>
                </ToolTipWrapper>
              </div>
              {plan?.offeringData?.map(
                (offeringData: any, offeringIndex: number) => {
                  return (
                    <div className={style.w_full} key={offeringIndex}>
                      {offeringIndex > 0 && (
                        <div
                          className={cn(style.cell_height_40, style.pt_2)}
                        ></div>
                      )}
                      {offeringData?.subOffering?.map(
                        (
                          subOffering: Array<boolean>,
                          subOfferingIndex: number,
                          self: Array<boolean>,
                        ) => (
                          <div
                            className={cn(
                              [style.cell_height_40, style.offering_cell],
                              {
                                [cn(style.offering_dashed, style.pb_2)]:
                                  subOfferingIndex == self?.length - 1 &&
                                  offeringIndex !==
                                  plan?.offeringData?.length - 1,
                              },
                            )}
                            key={subOfferingIndex}
                          >
                            {subOffering ? (
                              <img
                                src={getCDNAssetUrl(
                                  planIndex === selectedIndex
                                    ? CHECK_GREEN
                                    : CHECK_GREEN_FADE,
                                )}
                              />
                            ) : (
                              <img
                                src={getCDNAssetUrl(
                                  planIndex === selectedIndex
                                    ? CROSS_RED
                                    : CROSS_RED_FADE,
                                )}
                              />
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  );
                },
              )}
            </div>
          ))}
        </div>
      </section>
      {selectedPlan?.videoStripTitle && selectedPlan?.planVideoId && (
        <div
          onClick={handleDemoVideoRedirection}
          className={style.strip_wrapper}
        >
          <div className={style.strip_img_wrapper}>
            <img src={getCDNAssetUrl(POLYGON_BLACK)} alt="play" />
          </div>
          {selectedPlan?.videoStripTitle}
        </div>
      )}
      {openInfoModal && selectedContent && (
        <InfoModal
          selectedContent={selectedContent}
          open={openInfoModal}
          onClose={() => setOpenInfoModal(false)}
        />
      )}
    </div>
  );
};

export default PlanDescription;
